import axios from "axios"
import {getStationCode} from "../../network/NetworkUtil";
import {
    convertEpochMillisToLocalTime,
    convertSecondsToTimeFormat,
    formatAlias,
    getAssignmentEndTime,
    getDateInMMDDYYYYWithOffset,
    getTimeDiffFromCurrentTimeInHHMMSSFormat,
    isTimeValid,
    sortList
} from "../../util/Util";
import {
    ASSOCIATE_TAB_METRIC_KEY,
    ERROR_COUNT_METRIC,
    GLOBAL_METRIC_KEY,
    logErrorMetric,
    logOperationalMetric,
    MetricType,
    RSWS_ASSOCIATE_METRIC,
    RSWS_ROUTE_LIST_METRIC
} from "../../util/MetricUtil";
import {NO, SORT_DIRECTION, TIMEOUTS, YES} from "../../util/Constants";
import {getPickStatusColor, PICK_STATUS} from "../../util/EntityStatusManager";
import {Logger} from "../../util/Logger";
import {getAPIGatewayDataFromParent} from "../../handler/ParentDataHandler";

export function fetchAssociateMetrics(dispatcher, baseUrl, stationConfig) {
    getAssociateMetricsFromRSWS(dispatcher, baseUrl, stationConfig)
}

async function getAssociateMetricsFromRSWS(dispatcher, baseUrl, stationConfig) {
    try {
        const response = await getAssociateMetrics(baseUrl, stationConfig);
        let output = response && response.status === 200 ? response.data.associateMetricList : [];
        dispatcher({type: "RECEIVE_DATA", data: convertResponse(output)});
    } catch (error) {
        Logger.error("Exception in AssociateNetworkAPI.getAssociateMetricsFromRSWS", error);
        logErrorMetric(getStationCode(), GLOBAL_METRIC_KEY, ERROR_COUNT_METRIC);
    }
}

async function getAssociateMetrics(baseUrl, stationConfig) {
    const startTime = Date.now();
    const stationCode = getStationCode();
    try {
        const api = axios.create({
            baseURL: baseUrl,
            timeout: TIMEOUTS.RSWS_DEFAULT_API_TIMEOUT
        });
        var response;
        if (stationConfig && stationConfig.authEnabled) {
            const requestBody = {
                "nodeId": [stationCode],
                "process": ["PICK"]
            }
            const requestPayload = {
                resourcePath: "/apigw/associate/metric",
                httpMethod: "get",
                processName: "routeStaging",
                requestParams: requestBody
            }
            response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            response = await api.get("/rsws/associate/metric?nodeId=" + stationCode + "&process=PICK");
        }
        if (response && response.status === 200) {
            logOperationalMetric(startTime, stationCode, ASSOCIATE_TAB_METRIC_KEY, RSWS_ASSOCIATE_METRIC, MetricType.SUCCESS);
            return response;
        } else {
            var error = {
                response : response
            }
            logOperationalMetric(startTime, stationCode, ASSOCIATE_TAB_METRIC_KEY, RSWS_ASSOCIATE_METRIC, MetricType.FAILED, error);
        }

    } catch (e) {
        Logger.error("Exception in AssociateNetworkAPI.getAssociateMetrics", e);
        logOperationalMetric(startTime, stationCode, ASSOCIATE_TAB_METRIC_KEY, RSWS_ASSOCIATE_METRIC, MetricType.FAILED, e);
    }
    return null;
}

function convertResponse(associateMetricList) {
    const associateMetric = associateMetricList.filter(associate => associate.packageHandled > 0).map(associate => {
        associate['loginId'] = associate['alias'];
        associate['alias'] = formatAlias(associate['alias'].toLowerCase());
        return associate;
    })
    return {
        associatePerformance: associateMetric.map(metric => {
            metric.active = (metric.active) ? 'Yes' : 'No'
            return metric
        })
    };
}

export function fetchAssociateMetricsV2(dispatcher, baseUrl, stationConfig) {
    getAssociateMetricsFromStationRoutes(dispatcher, baseUrl, stationConfig)
}

async function getAssociateMetricsFromStationRoutes(dispatcher, baseUrl, stationConfig) {
    try {
        const response = await getStationRoutes(baseUrl, stationConfig);
        let output = response && response.status === 200 ? response.data.routes : [];
        dispatcher({type: "RECEIVE_DATA", data: getPickAssociateMetrics(output)});
    } catch (error) {
        Logger.error("Exception in AssociateNetworkAPI.getAssociateMetricsFromRSWS", error);
        logErrorMetric(getStationCode(), GLOBAL_METRIC_KEY, ERROR_COUNT_METRIC);
    }
}

async function getStationRoutes(baseUrl, stationConfig) {
    const startTime = Date.now();
    const stationCode = getStationCode();
    try {
        const api = axios.create({
            baseURL: baseUrl,
            timeout: TIMEOUTS.RSWS_GET_STATION_ROUTES_API_TIMEOUT
        });
        var response;
        if (stationConfig && stationConfig.authEnabled) {
            const requestBody = {
                "date": [getDateInMMDDYYYYWithOffset(0)]
            }
            const requestPayload = {
                resourcePath: "/apigw/route/station/" + stationCode,
                httpMethod: "get",
                processName: "routeStaging",
                requestParams: requestBody
            }
            response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            response = await api.get("/rsws/route/station/" + stationCode + "?date=" + getDateInMMDDYYYYWithOffset(0));
        }
        if (response && response.status === 200) {
            logOperationalMetric(startTime, stationCode, ASSOCIATE_TAB_METRIC_KEY, RSWS_ASSOCIATE_METRIC, MetricType.SUCCESS);
            return response;
        } else {
            var error = {
                response : response
            }
            logOperationalMetric(startTime, stationCode, ASSOCIATE_TAB_METRIC_KEY, RSWS_ASSOCIATE_METRIC, MetricType.FAILED, error);
        }
    } catch (e) {
        Logger.error("Exception in AssociateNetworkAPI.getStationRoutes", e);
        logOperationalMetric(startTime, stationCode, ASSOCIATE_TAB_METRIC_KEY, RSWS_ROUTE_LIST_METRIC, MetricType.FAILED, e);
    }
    return null;
}

function getPickAssociateMetrics(routes) {
    let allPicklists = [];

    routes.forEach(route => {
        if (route.picklists) {
            route.picklists.forEach(picklist => {
                if (picklist.associates) {
                    picklist.associates.forEach(associate => {
                        addAssociatePicklists(associate, picklist, allPicklists, route);
                    });
                }
            })
        }
    });

    const associateMap = allPicklists.reduce((map, picklist) => {
        if (map[picklist.alias]) {
            if (picklist.startTime < map[picklist.alias].minStartTime) {
                map[picklist.alias].minStartTime = picklist.startTime;
            }
            if (picklist.endTime > map[picklist.alias].maxEndTime) {
                map[picklist.alias].maxEndTime = picklist.endTime;
                map[picklist.alias].lastStatus = picklist.status;
            }
            map[picklist.alias].totalDuration = picklist.duration + map[picklist.alias].totalDuration;
            map[picklist.alias].picklists.push(picklist);
        } else {
            map[picklist.alias] = {
                ...picklist,

                /* Below are aggregated fields */
                picklists: [picklist],
                totalDuration: picklist.duration,
                minStartTime: picklist.startTime,
                maxEndTime: picklist.endTime,
                lastStatus: picklist.status
            }
        }
        return map;
    }, {});

    const associatePerformance = Object.values(associateMap).map(mapping => {
        const picklistCount = mapping.picklists.length;
        let totalDurationSeconds = mapping.totalDuration / 1000;
        let averageDuration = convertSecondsToTimeFormat(totalDurationSeconds / picklistCount);
        const activePicklist = findActivePicklistForAssociate(mapping.picklists);
        const isActive = activePicklist !== undefined;
        const inActivePeriodInSeconds = getInActivePeriod(isActive, activePicklist, mapping);
        const totalIdleTimeInSeconds = calculateTotalIdleTimeOfAssociate(mapping.picklists) / 1000;
        const activePicklistDurationInSeconds = activePicklist ? activePicklist.duration / 1000 : 0;
        const lastUpdatedLocation = getLastUpdatedLocation(isActive, activePicklist, mapping.picklists);
        sortList(mapping.picklists, "startTime", SORT_DIRECTION.ASC);
        return {
            ...mapping,
            active: isActive ? YES : NO,
            averageDuration: averageDuration,
            currentPicklist: activePicklist,
            currentPicklistId: activePicklist ? activePicklist.picklistId : "-",
            currentPicklistCode: activePicklist ? activePicklist.picklistCode : "-",
            inActivePeriodInSeconds: inActivePeriodInSeconds,
            inActivePeriod: inActivePeriodInSeconds ? convertSecondsToTimeFormat(inActivePeriodInSeconds) : "-",
            currentPicklistDurationInSeconds: activePicklistDurationInSeconds,
            currentPicklistDuration: isActive ? convertSecondsToTimeFormat(activePicklistDurationInSeconds) : "-",
            totalIdleTimeInSeconds: totalIdleTimeInSeconds,
            totalIdleTime: totalIdleTimeInSeconds ? convertSecondsToTimeFormat(totalIdleTimeInSeconds) : "-",
            picklists: mapping.picklists,
            lastLocation: lastUpdatedLocation
        }
    });
    return {
        associatePerformance
    }
}

function findActivePicklistForAssociate(picklists) {
    const sortedList = sortList(picklists, "startTime", SORT_DIRECTION.DESC);
    return sortedList.find(picklist => isActiveStatus(picklist.status));
}

function calculateTotalIdleTimeOfAssociate(picklists) {
    let totalIdleTime = 0;
    if (Array.isArray(picklists) && picklists.length > 1) {
        const sortedList = sortList(picklists, "startTime", SORT_DIRECTION.ASC);
        for (let index = 1; index < sortedList.length; index++) {
            const prevEndTime = sortedList[index - 1].endTime;
            const currStartTime = sortedList[index].startTime;
            if (isTimeValid(prevEndTime) && isTimeValid(currStartTime) && currStartTime >= prevEndTime) {
                totalIdleTime += currStartTime - prevEndTime;
            }
        }
    }
    return totalIdleTime;
}

function addAssociatePicklists(associate, picklist, allPicklists, route) {
    let startTime = associate.startTime;
    let endTime = getAssignmentEndTime(associate);
    if (associate.latest) {
        allPicklists.push({
            /* Picklist level fields */
            loginId: associate.alias,
            alias: formatAlias(associate.alias),
            status: picklist.status,
            startTime: startTime,
            endTime: endTime,
            duration: associate.pickingDuration,
            formattedStartTime: convertEpochMillisToLocalTime(startTime),
            formattedEndTime: endTime ? convertEpochMillisToLocalTime(endTime) : "-",
            formattedDuration: convertSecondsToTimeFormat(associate.pickingDuration / 1000),
            lastActivityTime: picklist.lastActivityTime,
            picklistId: picklist.id,
            picklistCode: picklist.code,
            routeId: route.routeId,
            routeCode: route.routeCode,
            progress: picklist.pickProgress ? `${picklist.pickProgress.completedCount}/${picklist.pickProgress.totalCount}` : "-",
            statusColor: getPickStatusColor(picklist.status),
            stationCode: route.stationCode,
            lastUpdatedLocation: picklist.lastUpdatedLocation,
            stagingLocation: route.stagingLocation
        })
    }
}

function getInActivePeriod(isActive, activePicklist, associateDetails) {
    if (isActive) {
        if (activePicklist && activePicklist.lastActivityTime) {
            return getTimeDiffFromCurrentTimeInHHMMSSFormat(activePicklist.lastActivityTime);
        }
        return 0;
    }
    return getTimeDiffFromCurrentTimeInHHMMSSFormat(associateDetails.maxEndTime);
}

function getLastUpdatedLocation (isActive, activePicklist, picklists) {
    if(isActive) {
        if(activePicklist) {
            return activePicklist.lastUpdatedLocation ? activePicklist.lastUpdatedLocation : "-";
        }
    }
    const sortedList = sortList(picklists, "startTime", SORT_DIRECTION.DESC);
    if(sortedList.length > 0 && sortedList[0].stagingLocation) {
        return sortedList[0].stagingLocation;
    }
    return "-";
}

function isActiveStatus(picklistStatus) {
    return picklistStatus && picklistStatus === PICK_STATUS.IN_PROGRESS;
}


// WEBPACK FOOTER //
// ./src/components/pickassociate/network/AssociateNetworkAPI.js